/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC, useEffect } from "react";
import { DropTargetParams } from "../../types/components/dragAndDrop";

const style = {
  border: "0",
  boxShadow: "unset",
  background: "transparent",
};

const DropTarget: FC<DropTargetParams> = (props: DropTargetParams) => {
  const dropTarget = React.useRef(null);
  const [isOver, setIsOver] = React.useState(false);
  const { children, hoverClass, className, onMoveX, targetDroppedId } = props;

  /**
   * Event handle drag over will bring all (css making) when mouse dragging over page.
   * @param event
   */
  const handleWindowMouseMove = (event) => {
    event.preventDefault();
    if (onMoveX) onMoveX(event?.clientX);
  };

  /**
   * Handle reset state (position when moving column in table) to default (0).
   */
  const dragEnd = (ev) => {
    ev.preventDefault();
    if (onMoveX) onMoveX(0);
  };

  useEffect(() => {
    window.addEventListener("dragover", handleWindowMouseMove);
    return () => window.removeEventListener("dragover", handleWindowMouseMove);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dragOver = (ev: React.DragEvent<HTMLDivElement>) => {
    ev.preventDefault();
    // @ts-ignore
    ev.dataTransfer.dropEffect = props.dropEffect;
  };

  /**
   * This event called when dragged.
   * @param
   */
  const drop = (ev: React.DragEvent<HTMLElement>) => {
    const { onItemDropped } = props;
    const droppedItem = ev.dataTransfer.getData("drag-item");
    // @ts-ignore
    ev.target.classList.remove(hoverClass);
    if (droppedItem) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      onItemDropped && onItemDropped(droppedItem, targetDroppedId);
    }
    setIsOver(false);
    // Reset Moving.
    if (onMoveX) onMoveX(0);
    ev.stopPropagation();
  };

  const dragEnter = (ev: React.DragEvent<HTMLElement>) => {
    const target = ev.target as HTMLElement;
    const { dataTransfer } = ev;
    if (dataTransfer == null) {
      return;
    }
    dataTransfer.dropEffect = props?.dropEffect || "none";
    if (hoverClass) {
      target.classList.add(hoverClass);
    }
    setIsOver(true);
  };

  const dragLeave = (ev: React.DragEvent<HTMLElement>) => {
    // @ts-ignore
    ev.target.classList.remove(hoverClass);
    setIsOver(false);
  };

  return (
    <div
      onDragOver={dragOver}
      onDrop={drop}
      onDragEnter={dragEnter}
      onDragLeave={dragLeave}
      onDragEnd={dragEnd}
      ref={dropTarget}
      className={className || ""}
      style={{
        ...style,
        ...(isOver ? { backgroundColor: "#cccccc", opacity: 0.5 } : {}),
      }}
    >
      {children}
    </div>
  );
};

export default DropTarget;
